import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useCustomerDetail } from '@/hooks/useCustomers';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { OrderStatusBadge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { StatCard } from '@/components/ui/StatCard';
import { formatCurrency, formatRelativeTime, formatDate } from '@/utils/format';

const segmentVariant: Record<string, 'purple' | 'info' | 'success' | 'default'> = { vip: 'purple', repeat: 'info', new: 'success', inactive: 'default' };

export function CustomerDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId: cidStr } = useParams();
  const customerId = Number(cidStr);
  const activeSiteId = useUIStore((s) => s.activeSiteId);
  const { data, isLoading, isError, refetch } = useCustomerDetail(activeSiteId, customerId);

  if (!activeSiteId) {
    return <EmptyState icon={UsersIcon} title={t('dashboard.selectStore')} />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2"><Skeleton className="h-64 w-full rounded-xl" /></div>
          <div className="space-y-4"><Skeleton className="h-40 w-full rounded-xl" /></div>
        </div>
      </div>
    );
  }

  if (isError || !data) return <ErrorState onRetry={() => refetch()} />;

  const { customer, recentOrders } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/customers')} leftIcon={<ArrowLeftIcon className="h-4 w-4" />}>{t('common.back')}</Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{customer.firstName} {customer.lastName}</h1>
        <Badge label={t(`customers.segments.${customer.segment}`)} variant={segmentVariant[customer.segment] ?? 'default'} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard title={t('customers.ordersCount')} value={String(customer.ordersCount)} />
            <StatCard title={t('customers.totalSpent')} value={formatCurrency(customer.totalSpent)} />
            <StatCard title={t('customers.averageOrder')} value={formatCurrency(customer.averageOrderValue)} />
            <StatCard title={t('customers.customerSince')} value={formatDate(customer.dateCreated, 'MM/yyyy')} />
          </div>

          {/* Recent Orders */}
          <Card title={t('customers.recentOrders')}>
            {recentOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      <th className="px-4 py-2">{t('orders.order')}</th>
                      <th className="px-4 py-2">{t('orders.total')}</th>
                      <th className="px-4 py-2">{t('orders.status')}</th>
                      <th className="px-4 py-2">{t('orders.date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.id} className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50" onClick={() => navigate(`/orders/${order.id}`)}>
                        <td className="px-4 py-3 font-medium text-brand-600 dark:text-brand-400">#{order.number}</td>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{formatCurrency(order.total, order.currency)}</td>
                        <td className="px-4 py-3"><OrderStatusBadge status={order.status} /></td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatRelativeTime(order.dateCreated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState icon={UsersIcon} title={t('orders.noOrders')} />
            )}
          </Card>
        </div>

        <div className="space-y-4">
          {/* Profile */}
          <Card title={t('customers.profile')}>
            <div className="flex items-center gap-4">
              <Avatar name={`${customer.firstName} ${customer.lastName}`} src={customer.avatarUrl} size="lg" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{customer.firstName} {customer.lastName}</p>
                <a href={`mailto:${customer.email}`} className="text-sm text-brand-600 hover:underline dark:text-brand-400">{customer.email}</a>
              </div>
            </div>
          </Card>

          {/* Billing */}
          <Card title={t('orders.billingAddress')}>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <p>{customer.billing.firstName} {customer.billing.lastName}</p>
              {customer.billing.company && <p>{customer.billing.company}</p>}
              <p>{customer.billing.address1}</p>
              {customer.billing.address2 && <p>{customer.billing.address2}</p>}
              <p>{customer.billing.city} {customer.billing.postcode}</p>
              <p>{customer.billing.country}</p>
            </div>
          </Card>

          {/* Shipping */}
          <Card title={t('orders.shippingAddress')}>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <p>{customer.shipping.firstName} {customer.shipping.lastName}</p>
              <p>{customer.shipping.address1}</p>
              {customer.shipping.address2 && <p>{customer.shipping.address2}</p>}
              <p>{customer.shipping.city} {customer.shipping.postcode}</p>
              <p>{customer.shipping.country}</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
